import type {PropertyDefinitionBase, PropertyValue, PropertyValuesRecord} from '@github-ui/custom-properties-types'
import {areEqualValues, getEmptyValueByType} from '@github-ui/custom-properties-types/helpers'
import {useDebounce} from '@github-ui/use-debounce'
import {type DependencyList, useCallback, useEffect, useMemo, useState} from 'react'

export interface PropertyField {
  value?: PropertyValue
  mixed: boolean
  changed: boolean
  propertyName: string
  error?: string
}

interface EditCustomPropertiesProps {
  propertyValues: PropertyValuesRecord[]
  editableDefinitions: PropertyDefinitionBase[]
  propertyValueValidator: (propertyName: string, value?: PropertyValue) => Promise<string | false | undefined>
}

export function useEditCustomProperties(
  {propertyValues, editableDefinitions, propertyValueValidator}: EditCustomPropertiesProps,
  deps: DependencyList = [],
) {
  const [originalMergedValues, setOriginalMergedValues] = useState(() =>
    mergeProperties(propertyValues, editableDefinitions),
  )

  const [errors, setErrors] = useState<Record<string, string>>({})

  const [modifiedProperties, setModifiedProperties] = useState<Record<string, {value: PropertyValue}>>({})
  useEffect(() => {
    setModifiedProperties({})
    setOriginalMergedValues(mergeProperties(propertyValues, editableDefinitions))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the implementation is the same as for the `useEffect` hook
  }, deps)

  function setPropertyValue(property: string, value: PropertyValue) {
    setModifiedProperties({...modifiedProperties, [property]: {value}})
    debouncedValidateError(property, value)
  }

  function revertPropertyValue(property: string) {
    const updatedProperties = {...modifiedProperties}
    delete updatedProperties[property]
    setModifiedProperties(updatedProperties)
    const updatedErrors = {...errors}
    delete updatedErrors[property] // the original value should be valid
    setErrors(updatedErrors)
  }

  function discardChanges() {
    setModifiedProperties({})
  }

  function commitChanges() {
    const modifiedPropertiesToCommit = Object.entries(modifiedProperties).reduce<Record<string, MergedValue>>(
      (acc, [propertyName, mergedValue]) => {
        acc[propertyName] = {value: mergedValue.value}
        return acc
      },
      {},
    )

    const committedProperties = {...originalMergedValues, ...modifiedPropertiesToCommit}

    setOriginalMergedValues(committedProperties)
    setModifiedProperties({})
  }

  const validateError = useCallback(
    async (propertyName: string, value?: PropertyValue) => {
      const error = await propertyValueValidator(propertyName, value)

      const updatedErrors = {...errors}
      delete updatedErrors[propertyName]
      if (error) {
        updatedErrors[propertyName] = error
      }
      setErrors(updatedErrors)
    },
    [errors, propertyValueValidator],
  )

  const debouncedValidateError = useDebounce(validateError, 300, {onChangeBehavior: 'cancel'})

  const propertyValuesMap = useMemo<Record<string, PropertyField>>(() => {
    const propertyValuesWithoutErrors = Object.entries({...originalMergedValues, ...modifiedProperties}).reduce<
      Record<string, PropertyField>
    >((acc, [propertyName, update]) => {
      const originalValue = originalMergedValues[propertyName]
      const isModified = propertyName in modifiedProperties

      const mixed = isModified ? false : !!originalValue?.mixed

      if (mixed) {
        acc[propertyName] = {propertyName, changed: false, mixed: true}
      } else {
        const changed = isModified && (originalValue?.mixed || !areEqualValues(originalValue?.value, update.value))
        const value = isModified ? update.value : originalValue?.value
        const newEntry: PropertyField = {value, changed, propertyName, mixed: false}

        acc[propertyName] = newEntry
      }

      return acc
    }, {})

    for (const entry of Object.entries(errors)) {
      const [propertyName, error] = entry
      const valueMap = propertyValuesWithoutErrors[propertyName]
      if (valueMap) {
        valueMap.error = error
      }
    }

    return propertyValuesWithoutErrors
  }, [modifiedProperties, originalMergedValues, errors])

  const propertyValuesErrors = (formErrors: Record<string, string>) => {
    setErrors({...errors, ...formErrors})
  }

  return {
    propertyValuesMap,
    setPropertyValue,
    revertPropertyValue,
    discardChanges,
    commitChanges,
    propertyValuesErrors,
  }
}

interface MergedValue {
  value?: PropertyValue
  mixed?: boolean
}

function mergeProperties(
  propertyValues: PropertyValuesRecord[],
  definitions: PropertyDefinitionBase[],
): Record<string, MergedValue> {
  const mergedValues: Record<string, MergedValue> = {}
  const propertiesCounter: Record<string, number> = {}
  for (const repoProperties of propertyValues) {
    for (const [propertyName, value] of Object.entries(repoProperties)) {
      const propertyValue = mergedValues[propertyName]
      if (!propertyValue) {
        mergedValues[propertyName] = {value}
      } else if (!areEqualValues(propertyValue.value, value)) {
        mergedValues[propertyName] = {mixed: true}
      }

      // Detect properties that are only present on some repos to consider them in the "mixed" state
      propertiesCounter[propertyName] ||= 0
      propertiesCounter[propertyName]++
    }
  }

  for (const [name, count] of Object.entries(propertiesCounter)) {
    const propertyValue = mergedValues[name]
    if (propertyValue && count < propertyValues.length) {
      mergedValues[name] = {mixed: true}
    }
  }

  // On form validation, the property map needs an object to display errors if they haven't changed, so initialize these with undefined
  for (const {propertyName} of definitions) {
    if (!mergedValues[propertyName]) {
      mergedValues[propertyName] = {value: undefined}
    }
  }

  return mergedValues
}

export function propertyFieldsToChangedPropertiesMap(
  definitions: PropertyDefinitionBase[],
  valuesMap: Record<string, PropertyField>,
): PropertyValuesRecord {
  return definitions.reduce<PropertyValuesRecord>((acc, def) => {
    const field = valuesMap[def.propertyName]

    if (field?.changed) {
      acc[def.propertyName] = field.value || getEmptyValueByType(def.valueType)
    }

    return acc
  }, {})
}
